// Molecular file parsers for converting job results to table data

import type {
  PDBAtom,
  CIFAtom,
  ResidueData,
  ChainData,
  LigandData,
  BindingData,
  ParsedMolecularData,
  ParsingLevel,
  ParsingOptions,
  MolecularParsingError,
  ParsingProgress
} from '../types/molecular';

// PDB file format constants
const PDB_RECORD_TYPES = {
  ATOM: 'ATOM  ',
  HETATM: 'HETATM',
  HEADER: 'HEADER',
  TITLE: 'TITLE ',
  SEQRES: 'SEQRES',
  HELIX: 'HELIX ',
  SHEET: 'SHEET ',
  END: 'END   '
};

// Standard amino acid three-letter codes
const AMINO_ACIDS = new Set([
  'ALA', 'ARG', 'ASN', 'ASP', 'CYS', 'GLN', 'GLU', 'GLY', 'HIS', 'ILE',
  'LEU', 'LYS', 'MET', 'PHE', 'PRO', 'SER', 'THR', 'TRP', 'TYR', 'VAL'
]);

/**
 * Parse PDB file content and extract atomic data
 */
export function parsePDBAtoms(
  content: string, 
  options: ParsingOptions = { level: 'atomic' }
): PDBAtom[] {
  const lines = content.split('\n');
  const atoms: PDBAtom[] = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    
    // Skip non-atom records
    if (!line.startsWith(PDB_RECORD_TYPES.ATOM) && !line.startsWith(PDB_RECORD_TYPES.HETATM)) {
      continue;
    }
    
    // Skip hydrogens if option is set
    if (!options.includeHydrogens && line.substring(76, 78).trim() === 'H') {
      continue;
    }
    
    try {
      const atom: PDBAtom = {
        serial: parseInt(line.substring(6, 11).trim()),
        atomName: line.substring(12, 16).trim(),
        residue: line.substring(17, 20).trim(),
        chain: line.substring(21, 22).trim(),
        residueNumber: parseInt(line.substring(22, 26).trim()),
        x: parseFloat(line.substring(30, 38).trim()),
        y: parseFloat(line.substring(38, 46).trim()),
        z: parseFloat(line.substring(46, 54).trim()),
        occupancy: parseFloat(line.substring(54, 60).trim()) || 1.0,
        tempFactor: parseFloat(line.substring(60, 66).trim()) || 0.0,
        element: line.substring(76, 78).trim() || line.substring(12, 14).trim().replace(/[0-9]/g, '')
      };
      
      // Apply chain filter if specified
      if (options.chainFilter && !options.chainFilter.includes(atom.chain)) {
        continue;
      }
      
      // Apply residue range filter if specified
      if (options.residueRange) {
        if (atom.residueNumber < options.residueRange.start || 
            atom.residueNumber > options.residueRange.end) {
          continue;
        }
      }
      
      atoms.push(atom);
      
      // Respect max atoms limit for performance
      if (options.maxAtoms && atoms.length >= options.maxAtoms) {
        break;
      }
      
    } catch (error) {
      console.warn(`Error parsing PDB line ${i + 1}: ${line}`, error);
      continue;
    }
  }
  
  return atoms;
}

/**
 * Convert PDB atoms to atomic-level table data
 */
export function convertPDBToAtomicTable(
  atoms: PDBAtom[], 
  options: ParsingOptions = { level: 'atomic' }
): ParsedMolecularData {
  const precision = options.precision || 3;
  
  const headers = [
    'Serial', 'Element', 'Atom Name', 'X', 'Y', 'Z', 
    'Residue', 'Res#', 'Chain', 'Occupancy', 'B-Factor'
  ];
  
  const rows = atoms.map(atom => [
    atom.serial.toString(),
    atom.element,
    atom.atomName,
    atom.x.toFixed(precision),
    atom.y.toFixed(precision),
    atom.z.toFixed(precision),
    atom.residue,
    atom.residueNumber.toString(),
    atom.chain,
    atom.occupancy.toFixed(2),
    atom.tempFactor.toFixed(2)
  ]);
  
  return {
    type: 'atomic',
    headers,
    rows,
    metadata: {
      atomCount: atoms.length,
      chainCount: new Set(atoms.map(a => a.chain)).size,
      residueCount: new Set(atoms.map(a => `${a.chain}:${a.residueNumber}`)).size
    }
  };
}

/**
 * Convert PDB atoms to residue-level summary table
 */
export function convertPDBToResidueTable(
  atoms: PDBAtom[], 
  options: ParsingOptions = { level: 'residue' }
): ParsedMolecularData {
  const precision = options.precision || 3;
  
  // Group atoms by residue
  const residueMap = new Map<string, PDBAtom[]>();
  
  atoms.forEach(atom => {
    const key = `${atom.chain}:${atom.residueNumber}:${atom.residue}`;
    if (!residueMap.has(key)) {
      residueMap.set(key, []);
    }
    residueMap.get(key)!.push(atom);
  });
  
  const headers = [
    'Position', 'Residue', 'Chain', 'Center X', 'Center Y', 'Center Z', 
    'Atom Count', 'Avg B-Factor', 'Type'
  ];
  
  const rows: string[][] = [];
  
  residueMap.forEach((residueAtoms, key) => {
    const [chain, resNum, resName] = key.split(':');
    
    // Calculate geometric center
    const centerX = residueAtoms.reduce((sum, a) => sum + a.x, 0) / residueAtoms.length;
    const centerY = residueAtoms.reduce((sum, a) => sum + a.y, 0) / residueAtoms.length;
    const centerZ = residueAtoms.reduce((sum, a) => sum + a.z, 0) / residueAtoms.length;
    
    // Calculate average B-factor
    const avgBFactor = residueAtoms.reduce((sum, a) => sum + a.tempFactor, 0) / residueAtoms.length;
    
    // Determine residue type
    const type = AMINO_ACIDS.has(resName) ? 'Amino Acid' : 'Other';
    
    rows.push([
      resNum,
      resName,
      chain,
      centerX.toFixed(precision),
      centerY.toFixed(precision),
      centerZ.toFixed(precision),
      residueAtoms.length.toString(),
      avgBFactor.toFixed(2),
      type
    ]);
  });
  
  // Sort by chain and residue number
  rows.sort((a, b) => {
    const chainCompare = a[2].localeCompare(b[2]);
    if (chainCompare !== 0) return chainCompare;
    return parseInt(a[0]) - parseInt(b[0]);
  });
  
  return {
    type: 'residue',
    headers,
    rows,
    metadata: {
      atomCount: atoms.length,
      residueCount: residueMap.size,
      chainCount: new Set(atoms.map(a => a.chain)).size
    }
  };
}

/**
 * Convert PDB atoms to chain-level summary table
 */
export function convertPDBToChainTable(
  atoms: PDBAtom[], 
  options: ParsingOptions = { level: 'chain' }
): ParsedMolecularData {
  // Group atoms by chain
  const chainMap = new Map<string, PDBAtom[]>();
  
  atoms.forEach(atom => {
    if (!chainMap.has(atom.chain)) {
      chainMap.set(atom.chain, []);
    }
    chainMap.get(atom.chain)!.push(atom);
  });
  
  const headers = [
    'Chain ID', 'Atom Count', 'Residue Count', 'Start Res', 'End Res', 
    'Avg B-Factor', 'Primary Type'
  ];
  
  const rows: string[][] = [];
  
  chainMap.forEach((chainAtoms, chainId) => {
    // Get unique residues in this chain
    const residues = new Set(chainAtoms.map(a => `${a.residueNumber}:${a.residue}`));
    const residueNumbers = chainAtoms.map(a => a.residueNumber);
    const residueNames = chainAtoms.map(a => a.residue);
    
    const startRes = Math.min(...residueNumbers);
    const endRes = Math.max(...residueNumbers);
    
    // Calculate average B-factor
    const avgBFactor = chainAtoms.reduce((sum, a) => sum + a.tempFactor, 0) / chainAtoms.length;
    
    // Determine chain type based on residue composition
    const aminoAcidCount = residueNames.filter(name => AMINO_ACIDS.has(name)).length;
    const primaryType = aminoAcidCount > residueNames.length * 0.5 ? 'Protein' : 'Other';
    
    rows.push([
      chainId,
      chainAtoms.length.toString(),
      residues.size.toString(),
      startRes.toString(),
      endRes.toString(),
      avgBFactor.toFixed(2),
      primaryType
    ]);
  });
  
  // Sort by chain ID
  rows.sort((a, b) => a[0].localeCompare(b[0]));
  
  return {
    type: 'chain',
    headers,
    rows,
    metadata: {
      atomCount: atoms.length,
      chainCount: chainMap.size,
      residueCount: new Set(atoms.map(a => `${a.chain}:${a.residueNumber}`)).size
    }
  };
}

/**
 * Main PDB parsing function that routes to appropriate converter
 */
export function parsePDBFile(
  content: string, 
  level: ParsingLevel = 'atomic',
  options: ParsingOptions = { level }
): ParsedMolecularData {
  const startTime = Date.now();
  
  try {
    // Parse atoms from PDB content
    const atoms = parsePDBAtoms(content, options);
    
    if (atoms.length === 0) {
      throw new Error('No valid atoms found in PDB file');
    }
    
    let result: ParsedMolecularData;
    
    // Route to appropriate converter based on level
    switch (level) {
      case 'atomic':
        result = convertPDBToAtomicTable(atoms, options);
        break;
      case 'residue':
        result = convertPDBToResidueTable(atoms, options);
        break;
      case 'chain':
        result = convertPDBToChainTable(atoms, options);
        break;
      case 'binding':
        throw new Error(`PDB format does not support 'binding' parsing level. This level is only available for CIF files containing active site data.`);
      default:
        throw new Error(`Unsupported parsing level: ${level}`);
    }
    
    // Add parsing metadata
    result.metadata.parseTime = Date.now() - startTime;
    
    return result;
    
  } catch (error) {
    throw new Error(`PDB parsing failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

/**
 * Parse CIF file content and extract atomic data
 */
export function parseCIFAtoms(
  content: string,
  options: ParsingOptions = { level: 'atomic' }
): CIFAtom[] {
  const lines = content.split('\n');
  const atoms: CIFAtom[] = [];
  
  // Find the atom_site loop
  let inAtomLoop = false;
  let headerLine = -1;
  const columnMap = new Map<string, number>();
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    // Start of atom_site loop
    if (line.startsWith('loop_')) {
      // Check if next lines contain atom_site labels
      for (let j = i + 1; j < lines.length && lines[j].startsWith('_atom_site'); j++) {
        if (j === i + 1) {
          inAtomLoop = true;
          headerLine = j;
          break;
        }
      }
      continue;
    }
    
    // Parse column headers
    if (inAtomLoop && line.startsWith('_atom_site')) {
      const columnName = line.split('.')[1] || line;
      columnMap.set(columnName, columnMap.size);
      continue;
    }
    
    // Parse atom data
    if (inAtomLoop && !line.startsWith('_') && !line.startsWith('#') && line.length > 0) {
      // Stop if we hit a new loop or section
      if (line.startsWith('loop_') || line.startsWith('data_')) {
        break;
      }
      
      const fields = line.split(/\s+/);
      
      try {
        // Extract data based on column mapping
        const atom: CIFAtom = {
          serial: parseInt(fields[columnMap.get('id') || 0] || '0'),
          element: fields[columnMap.get('type_symbol') || 1] || '',
          atomName: fields[columnMap.get('label_atom_id') || 2] || '',
          x: parseFloat(fields[columnMap.get('Cartn_x') || 3] || '0'),
          y: parseFloat(fields[columnMap.get('Cartn_y') || 4] || '0'),
          z: parseFloat(fields[columnMap.get('Cartn_z') || 5] || '0'),
          residue: fields[columnMap.get('label_comp_id') || 6] || '',
          residueNumber: parseInt(fields[columnMap.get('label_seq_id') || 7] || '0'),
          chain: fields[columnMap.get('label_asym_id') || 8] || '',
          occupancy: parseFloat(fields[columnMap.get('occupancy') || 9] || '1.0'),
          tempFactor: parseFloat(fields[columnMap.get('B_iso_or_equiv') || 10] || '0.0'),
          authSeqId: parseInt(fields[columnMap.get('auth_seq_id') || 0] || '0'),
          labelSeqId: parseInt(fields[columnMap.get('label_seq_id') || 0] || '0'),
          formalCharge: parseInt(fields[columnMap.get('formal_charge') || 0] || '0')
        };
        
        // Apply filters
        if (options.chainFilter && !options.chainFilter.includes(atom.chain)) {
          continue;
        }
        
        if (!options.includeHydrogens && atom.element === 'H') {
          continue;
        }
        
        atoms.push(atom);
        
        if (options.maxAtoms && atoms.length >= options.maxAtoms) {
          break;
        }
        
      } catch (error) {
        console.warn(`Error parsing CIF line ${i + 1}: ${line}`, error);
        continue;
      }
    }
  }
  
  return atoms;
}

/**
 * Extract ligand information from CIF file
 */
export function parseCIFLigands(content: string): LigandData[] {
  const lines = content.split('\n');
  const ligands: LigandData[] = [];
  
  // Look for ligand definitions in chem_comp section
  let inChemCompLoop = false;
  const columnMap = new Map<string, number>();
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    if (line.startsWith('loop_')) {
      // Check for chem_comp loop
      for (let j = i + 1; j < lines.length && lines[j].startsWith('_chem_comp'); j++) {
        if (j === i + 1) {
          inChemCompLoop = true;
          break;
        }
      }
      continue;
    }
    
    if (inChemCompLoop && line.startsWith('_chem_comp')) {
      const columnName = line.split('.')[1] || line;
      columnMap.set(columnName, columnMap.size);
      continue;
    }
    
    if (inChemCompLoop && !line.startsWith('_') && line.length > 0) {
      if (line.startsWith('loop_') || line.startsWith('data_')) {
        break;
      }
      
      const fields = line.split(/\s+/);
      
      try {
        const ligand: LigandData = {
          id: fields[columnMap.get('id') || 0] || '',
          name: fields[columnMap.get('name') || 1] || '',
          formula: fields[columnMap.get('formula') || 2] || '',
          weight: parseFloat(fields[columnMap.get('formula_weight') || 3] || '0'),
          centerX: 0,
          centerY: 0,
          centerZ: 0,
          atomCount: 0
        };
        
        // Skip if it's a standard amino acid
        if (!AMINO_ACIDS.has(ligand.id)) {
          ligands.push(ligand);
        }
        
      } catch (error) {
        console.warn(`Error parsing ligand data: ${line}`, error);
      }
    }
  }
  
  return ligands;
}

/**
 * Convert CIF data to binding site analysis table
 */
export function convertCIFToBindingTable(
  atoms: CIFAtom[],
  ligands: LigandData[],
  options: ParsingOptions = { level: 'binding' }
): ParsedMolecularData {
  const precision = options.precision || 2;
  
  // Separate protein atoms and ligand atoms
  const proteinAtoms = atoms.filter(atom => AMINO_ACIDS.has(atom.residue));
  const ligandAtoms = atoms.filter(atom => !AMINO_ACIDS.has(atom.residue) && atom.residue !== 'HOH');
  
  const headers = [
    'Residue', 'Chain', 'Distance (Å)', 'Interaction Type', 'Contact Atoms', 'Accessibility'
  ];
  
  const rows: string[][] = [];
  const bindingCutoff = 5.0; // Angstroms
  
  // Group protein atoms by residue
  const residueMap = new Map<string, CIFAtom[]>();
  proteinAtoms.forEach(atom => {
    const key = `${atom.residue}${atom.residueNumber}${atom.chain}`;
    if (!residueMap.has(key)) {
      residueMap.set(key, []);
    }
    residueMap.get(key)!.push(atom);
  });
  
  // Calculate distances and interactions
  residueMap.forEach((residueAtoms, residueKey) => {
    const residue = residueAtoms[0];
    let minDistance = Infinity;
    const contactAtoms: string[] = [];
    
    // Find closest ligand atoms to this residue
    residueAtoms.forEach(proteinAtom => {
      ligandAtoms.forEach(ligandAtom => {
        const distance = Math.sqrt(
          Math.pow(proteinAtom.x - ligandAtom.x, 2) +
          Math.pow(proteinAtom.y - ligandAtom.y, 2) +
          Math.pow(proteinAtom.z - ligandAtom.z, 2)
        );
        
        if (distance < bindingCutoff) {
          minDistance = Math.min(minDistance, distance);
          contactAtoms.push(`${proteinAtom.atomName}-${ligandAtom.atomName}`);
        }
      });
    });
    
    // Only include residues that are close to ligands
    if (minDistance < bindingCutoff) {
      // Determine interaction type based on distance and atom types
      let interactionType = 'Van der Waals';
      if (minDistance < 3.5) {
        interactionType = 'Hydrogen Bond';
      } else if (minDistance < 4.0) {
        interactionType = 'Electrostatic';
      } else if (minDistance < 4.5) {
        interactionType = 'Hydrophobic';
      }
      
      // Simple accessibility calculation (placeholder)
      const accessibility = residueAtoms.length > 8 ? 'Buried' : 'Exposed';
      
      rows.push([
        `${residue.residue}${residue.residueNumber}`,
        residue.chain,
        minDistance.toFixed(precision),
        interactionType,
        contactAtoms.slice(0, 3).join(', '), // Show first 3 contacts
        accessibility
      ]);
    }
  });
  
  // Sort by distance
  rows.sort((a, b) => parseFloat(a[2]) - parseFloat(b[2]));
  
  return {
    type: 'binding',
    headers,
    rows,
    metadata: {
      atomCount: atoms.length,
      residueCount: residueMap.size,
      chainCount: new Set(atoms.map(a => a.chain)).size
    }
  };
}

/**
 * Main CIF parsing function
 */
export function parseCIFFile(
  content: string, 
  level: ParsingLevel = 'binding',
  options: ParsingOptions = { level }
): ParsedMolecularData {
  const startTime = Date.now();
  
  try {
    const atoms = parseCIFAtoms(content, options);
    
    if (atoms.length === 0) {
      throw new Error('No valid atoms found in CIF file');
    }
    
    let result: ParsedMolecularData;
    
    switch (level) {
      case 'atomic':
        // Convert CIF atoms to PDB atom format for compatibility
        const pdbAtoms: PDBAtom[] = atoms.map(atom => ({
          serial: atom.serial,
          element: atom.element,
          atomName: atom.atomName,
          x: atom.x,
          y: atom.y,
          z: atom.z,
          residue: atom.residue,
          residueNumber: atom.residueNumber,
          chain: atom.chain,
          occupancy: atom.occupancy,
          tempFactor: atom.tempFactor
        }));
        result = convertPDBToAtomicTable(pdbAtoms, options);
        break;
        
      case 'residue':
        const pdbAtomsRes: PDBAtom[] = atoms.map(atom => ({
          serial: atom.serial,
          element: atom.element,
          atomName: atom.atomName,
          x: atom.x,
          y: atom.y,
          z: atom.z,
          residue: atom.residue,
          residueNumber: atom.residueNumber,
          chain: atom.chain,
          occupancy: atom.occupancy,
          tempFactor: atom.tempFactor
        }));
        result = convertPDBToResidueTable(pdbAtomsRes, options);
        break;
        
      case 'chain':
        const pdbAtomsChain: PDBAtom[] = atoms.map(atom => ({
          serial: atom.serial,
          element: atom.element,
          atomName: atom.atomName,
          x: atom.x,
          y: atom.y,
          z: atom.z,
          residue: atom.residue,
          residueNumber: atom.residueNumber,
          chain: atom.chain,
          occupancy: atom.occupancy,
          tempFactor: atom.tempFactor
        }));
        result = convertPDBToChainTable(pdbAtomsChain, options);
        break;
        
      case 'binding':
        const ligands = parseCIFLigands(content);
        result = convertCIFToBindingTable(atoms, ligands, options);
        break;
        
      default:
        throw new Error(`Unsupported parsing level: ${level}`);
    }
    
    result.metadata.parseTime = Date.now() - startTime;
    return result;
    
  } catch (error) {
    throw new Error(`CIF parsing failed: ${error instanceof Error ? error.message : String(error)}`);
  }
}

/**
 * Detect file format from content
 */
export function detectFileFormat(content: string): 'pdb' | 'cif' | 'unknown' {
  // Trim content and get first few lines for better detection
  const trimmedContent = content.trim();
  const firstLines = trimmedContent.split('\n').slice(0, 20).join('\n');
  
  // Check for CIF format indicators first (more specific)
  // CIF files typically start with data_ or have loop_ structures
  if (trimmedContent.startsWith('data_') || 
      firstLines.includes('loop_') ||
      firstLines.includes('_atom_site.') ||
      firstLines.includes('_cell.') ||
      firstLines.includes('_symmetry.')) {
    return 'cif';
  }
  
  // Check for PDB format indicators
  // PDB files have fixed-width columns starting with specific keywords
  if (firstLines.match(/^(ATOM  |HETATM|HEADER|TITLE |REMARK|SEQRES)/m)) {
    return 'pdb';
  }
  
  // Additional PDB check for files that might not start with standard records
  if (content.includes('ATOM  ') || content.includes('HETATM')) {
    return 'pdb';
  }
  
  return 'unknown';
}

/**
 * Main parsing function that auto-detects format and routes appropriately
 */
export function parseMolecularFile(
  content: string,
  level: ParsingLevel = 'atomic',
  options: ParsingOptions = { level }
): ParsedMolecularData {
  const format = detectFileFormat(content);
  
  switch (format) {
    case 'pdb':
      return parsePDBFile(content, level, options);
    case 'cif':
      return parseCIFFile(content, level, options);
    default:
      // Provide more helpful error message
      const preview = content.substring(0, 200).replace(/\n/g, ' ');
      throw new Error(`Unable to detect molecular file format. File should be in PDB or CIF format. File preview: "${preview}..."`);
  }
}

/**
 * Utility function to validate parsing options
 */
export function validateParsingOptions(options: ParsingOptions): string[] {
  const warnings: string[] = [];
  
  if (options.maxAtoms && options.maxAtoms < 100) {
    warnings.push('Very low atom limit may result in incomplete data');
  }
  
  if (options.precision && (options.precision < 1 || options.precision > 6)) {
    warnings.push('Precision should be between 1 and 6 decimal places');
  }
  
  if (options.residueRange) {
    if (options.residueRange.start >= options.residueRange.end) {
      warnings.push('Invalid residue range: start must be less than end');
    }
  }
  
  return warnings;
}